import { InfoStep } from '../../../../backend-process/backend-process-presentation/backend-process-presentation.component';
import { ProductsSummaryComponent } from '../../products-summary.component';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { DataChangeType, TextNodeType } from '@mwe/models';
import {
  formatCurrencyValue,
  getAddressGroupFormatted,
  getAddressPostCodeFormatted,
  getAddressStreetFormatted,
  isProductCategory,
} from '@mwe/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AddressComponent,
  BasicCardComponent,
  HeadlineComponent,
  HeadlineService,
  InfoboxComponent,
  NextStepsComponent,
  ProductsSummaryInvoiceOldComponent,
  TariffSelectionContainerComponent,
  WizardProductCardComponent,
} from '@mwe/ui/components';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ProductCategoryEnum } from '@mwe/constants';
import { EnvironmentService, InternetStore } from '@mwe/services';
import { LowerCasePipe } from '@angular/common';
import { FormatPhoneNumberPipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-products-summary-redesign-internet',
  templateUrl: './products-summary-redesign-internet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HeadlineService],
  imports: [
    AddressComponent,
    HeadlineComponent,
    TariffSelectionContainerComponent,
    BasicCardComponent,
    WizardProductCardComponent,
    TranslateDirective,
    ProductsSummaryInvoiceOldComponent,
    NextStepsComponent,
    LowerCasePipe,
    FormatPhoneNumberPipe,
    InfoboxComponent,
    TranslatePipe,
  ],
})
export class ProductsSummaryRedesignInternetComponent extends ProductsSummaryComponent {
  protected readonly headlineService = inject(HeadlineService);
  private readonly internetStore = inject(InternetStore);
  private readonly environmentService = inject(EnvironmentService);

  headlineReference = input<TextNodeType>('h2');
  isSummaryProductsFromWevTitleVisible = input<boolean>(false);
  infoSteps = input<InfoStep[]>([]);

  readonly selectedTariff = computed(() => this.selectedTariffs()?.at(0));

  override readonly formattedAddress = computed(() => getAddressGroupFormatted(this.address()));
  readonly formattedAddressStreet = computed(() => getAddressStreetFormatted(this.address()));
  readonly formattedPostCode = computed(() => getAddressPostCodeFormatted(this.address()));
  readonly selectedCategory = computed(() => this.internetStore.selectedPrimaryCategory() ?? this.selectedTariff()?.category);
  readonly isMobileInternet = computed(() => this.internetStore.selectedCategories()?.[0] === ProductCategoryEnum['MOBILES INTERNET']);

  readonly isNewInternetOrder = computed(() => (this.internetStore?.orderType() ?? 'IO') === DataChangeType.InternetOrder);

  readonly preisUnit = computed(() => this.selectedTariff()?.priceIndication?.arbeitspreisUnit);

  readonly tariffName = computed(() => this.selectedTariff()?.priceIndication?.tarif?.tarifName);
  readonly tariffCostReduced = computed(() => formatCurrencyValue(this.selectedTariff()?.priceIndication?.grundpreisRabattiert));
  private readonly selectedTvProductKey = computed(() => this.selectedTariff()?.userSelection?.at(0)?.option ?? '');
  readonly tvOptionName = computed(() => {
    return (
      this.selectedTariff()?.priceIndication?.internet?.tvKosten?.find(tc => tc.produktKey == this.selectedTvProductKey())?.tvOption ?? ''
    );
  });
  readonly tvOptionCostReduced = computed(() => {
    return formatCurrencyValue(
      this.selectedTariff()?.priceIndication?.internet?.tvKosten?.find(tc => tc.produktKey == this.selectedTvProductKey())?.preis,
    );
  });

  readonly totalCostReduced = computed(() => {
    const fullCost = this.selectedTariff()?.priceIndication?.internet?.gesamtKosten?.find(cost => cost.zeitraum === 'reduziert');
    const index = fullCost?.tvOption?.indexOf(this.tvOptionName());

    return formatCurrencyValue(fullCost?.preis?.at(index));
  });

  readonly tariffCost = computed(() => formatCurrencyValue(this.selectedTariff()?.priceIndication?.grundpreis));
  readonly tvOptionCost = computed(() => {
    return formatCurrencyValue(
      this.selectedTariff()?.priceIndication?.internet?.tvKosten?.find(tc => tc.produktKey == this.selectedTvProductKey())?.preis,
    );
  });

  readonly totalCost = computed(() => {
    const fullCost = this.selectedTariff()?.priceIndication?.internet?.gesamtKosten?.find(cost => cost.zeitraum === 'normal');
    const index = fullCost?.tvOption?.indexOf(this.tvOptionName());

    return formatCurrencyValue(fullCost?.preis?.at(index));
  });

  readonly overallTotalCost = computed(() => {
    const fullCost = this.selectedTariff()?.priceIndication?.internet?.gesamtKosten?.find(cost => cost.zeitraum === 'gesamt');
    const index = fullCost?.tvOption?.indexOf(this.tvOptionName());

    return formatCurrencyValue(fullCost?.preis?.at(index));
  });

  constructor() {
    super();
    effect(() => {
      this.headlineService.setReferenceHeadline(this.headlineReference());
    });
  }

  getTariffSheetUrls(): Record<string, string> {
    const pdfUrl = this.environmentService.getDocumentsUrl();
    const tariffSheetUrls: Record<string, string> = {};

    for (const produkt of this.internetStore.productsForSelection().produkte) {
      const tarifKey = produkt?.tarif?.ISUTarifKey;
      if (!tarifKey) {
        continue;
      }
      if (isProductCategory(produkt.sparte?.toUpperCase(), ProductCategoryEnum.INTERNET)) {
        tariffSheetUrls[tarifKey] = pdfUrl.concat('/eblb-nonanet-glasfaser/');
      } else if (isProductCategory(produkt?.sparte?.toUpperCase(), ProductCategoryEnum['MOBILES INTERNET'])) {
        tariffSheetUrls[tarifKey] = pdfUrl.concat('/eblb-nonanet-5g-cube/');
      }
      tariffSheetUrls['tvTarif'] = pdfUrl.concat('/eblb-nonanet-tv');
      tariffSheetUrls['tvProducts'] = this.environmentService.getWienenergieWebsiteUrl().concat('/privat/produkte/internet-und-tv/tv/');
    }

    return tariffSheetUrls;
  }
}
